/* eslint-disable react-hooks/exhaustive-deps */
import { RoleFunction } from '@eagle/common';
import { LastThingEvent, PersonThingRangeResponse, Thing, ThingDataSubscriptionInfoResult, ThingLifeCycleStateResponse, ThingType } from '@eagle/core-data-types';
import { Location } from '@eagle/data-function-types';
import { CacheDataTypes, captureException, createThingFaultStatusLookup, DEFAULT_THING_MAP_POPUP, ErrorMessage, evaluate, LastThingEventsByFeature, MapPanes, MapPopup, ThingFaultStatus, Undefinable, useAuth, useAuthenticated, useBoolFlag, useCustomRoutes, useFetchAllCache, useFetchOneCache, useHasAuthorization, useLastThingState, useListSearch, useMapContext, usePromise, useSmallScreen, useThingsFaultStatus, useUiTemplate, validateLocationType } from '@eagle/react-common';
import { useTheme } from '@mui/material';
import L from 'leaflet';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import { useLocation, useNavigate } from 'react-router-dom';
import { LocationState } from './thing-map-navigator';

interface ThingPopupProps {
  thingId: Undefinable<string>;
}

interface ThingPopupTemplateProps {
  context: object;
  template: object;
}

const ThingPopupTemplate: FC<ThingPopupTemplateProps> = ({ context, template }) => <>{evaluate(template, context)}</>;

const toPopupFaultsContext = (faults?: ThingFaultStatus): Record<string, unknown> | undefined => {
  if (!faults) return undefined;

  return {
    totals: {
      activeCount: faults.activeFaultCount,
      totalFaults: faults.totalFaultCount,
    },
    warning: faults.warning,
    status: faults.status,
    latestFaultOccurredAt: faults.latestFaultOccurredAt,
    activeFaults: faults.activeFaults,
  };
};

export const ThingPopup: FC<ThingPopupProps> = ({ thingId }) => {
  const { restClient } = useAuthenticated();
  const { state: authState } = useAuth();
  const { things: customRoutesThings } = useCustomRoutes();
  const { enqueueSnackbar } = useSnackbar();
  const { setSavedSearchPosition, setMapDimming, selectedThingLocation, publishLastThingEvents } = useMapContext();
  const { template, loaded: templateLoaded } = useUiTemplate('thing-map-popup', DEFAULT_THING_MAP_POPUP);
  const [entityId, setEntityId] = useState<string>();
  const [shouldOpenPopup, setShouldOpenPopup] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [locationData, setLocationData] = useState<Undefinable<Location>>();
  const [lastThingEvent, setLastThingEvent] = useState<LastThingEvent>();
  const [lastThingEvents, setLastThingEvents] = useState<LastThingEventsByFeature>();
  const location = useLocation();
  const map = useMap();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  const shouldFly = state?.shouldFly ?? true;
  const isSmallScreen = useSmallScreen();
  const thingCache = useFetchOneCache(CacheDataTypes.THING);
  const thingTypeCache = useFetchAllCache(CacheDataTypes.THING_TYPE);
  const { setText } = useListSearch();
  const { hasAuthorization } = useHasAuthorization(authState);
  const LIFE_CYCLE_VIEWER = [RoleFunction.LIFE_CYCLE_VIEWER] as const;
  const hasLifeCycleViewerPermissions = hasAuthorization(LIFE_CYCLE_VIEWER);
  const hasAdminLifecycleFeature = useBoolFlag('admin-life-cycle-feature');
  const getLifeCycleData = hasAdminLifecycleFeature && thingId && hasLifeCycleViewerPermissions;
  const theme = useTheme();
  const navigateAfterCloseTimeoutRef = useRef<number | null>(null);
  const initialSnapPoint = shouldFly ? 'peek' : 'full';

  const [fetchState] = useLastThingState();
  const { data: thingsFaultStatusData } = useThingsFaultStatus(
    {
      thingIds: thingId ? [thingId] : [],
      range: 30,
      batchSize: 100,
    },
    {
      enabled: Boolean(thingId),
    },
  );
  const faultsByThingId = createThingFaultStatusLookup(thingsFaultStatusData?.items);

  useEffect(() => {
    if (!thingId) return;
    let isCurrent = true;

    fetchState(thingId).then((events) => {
      if (!isCurrent) return;

      setLastThingEvents(events?.byFeature);
      if (events) publishLastThingEvents(thingId, events.byFeature);

      const locationUpdate = events?.byFeature.tracking?.['location-update'];
      if (locationUpdate) {
        setLastThingEvent(events.latest);
        setLocationData(validateLocationType(() => locationUpdate.data.location));
      }
    }).catch(captureException);

    return () => {
      isCurrent = false;
    };
  }, [fetchState, thingId]);

  const arePositionsEqual = useCallback((pos1: L.LatLng, pos2: L.LatLng, precision = 4) => {
    const factor = Math.pow(10, precision);
    return (
      Math.round(pos1.lat * factor) === Math.round(pos2.lat * factor) &&
      Math.round(pos1.lng * factor) === Math.round(pos2.lng * factor)
    );
  }, []);

  useEffect(() => {
    if (!selectedThingLocation || thingId !== selectedThingLocation.thingId) return;
    if (!thingId || thingId !== selectedThingLocation?.thingId) return;

    const newPosition = new L.LatLng(selectedThingLocation.latitude, selectedThingLocation.longitude);
    if (!locationData) {
      setLocationData({
        latitude: newPosition.lat,
        longitude: newPosition.lng,
        source: '',
      });
      return;
    }

    const currentPosition = new L.LatLng(locationData.latitude, locationData.longitude);

    if (arePositionsEqual(newPosition, currentPosition)) return;

    setLocationData((prev) => ({
      ...prev,
      latitude: newPosition.lat,
      longitude: newPosition.lng,
      source: prev?.source || '',
    }));
  }, [selectedThingLocation, isPopupOpen, thingId, locationData, arePositionsEqual]);

  const openPopup = (): void => {
    setMapDimming('things', true);
    setShouldOpenPopup(true);
    setIsPopupOpen(false);
    setEntityId(thingId);
  };

  const closePopup = (): void => {
    setMapDimming('things', false);
    setShouldOpenPopup(false);
    setIsPopupOpen(false);
  };

  const closePopupAndNavigate = (navigateFn: () => void): void => {
    closePopup();

    if (navigateAfterCloseTimeoutRef.current !== null) {
      window.clearTimeout(navigateAfterCloseTimeoutRef.current);
      navigateAfterCloseTimeoutRef.current = null;
    }

    const navigationDelay = isSmallScreen ? theme.transitions.duration.leavingScreen : 0;
    navigateAfterCloseTimeoutRef.current = window.setTimeout(() => {
      navigateAfterCloseTimeoutRef.current = null;
      navigateFn();
    }, navigationDelay);
  };

  const handleCloseButtonClick = (): void => {
    setText('');
    closePopupAndNavigate(() => {
      if (!state?.previousLocations) {
        setSavedSearchPosition(null);
        navigate(`/map/${customRoutesThings}`, {
          state: {
            shouldFly: false,
          },
        });
        return;
      }
      setSavedSearchPosition(null);
      navigate(-1);
    });
  };

  const handleEscapeKeyDown = (e: KeyboardEvent): void => {
    if (!thingId || e.key !== 'Escape') return;
    handleCloseButtonClick();
  };

  const handleMapClick = (): void => {
    if (!thingId) return;
    closePopupAndNavigate(() => {
      setSavedSearchPosition(null);
      navigate(`/map/${customRoutesThings}`, {
        state: {
          shouldFly: false,
        },
      });
    });
  };

  const [thing, thingError, thingStatus] = usePromise<Undefinable<Thing>>(
    () => {
      if (!entityId) return Promise.resolve(undefined);
      return thingCache.one(entityId);
    },
    [entityId]
  );

  const [thingPersons, thingPersonsError, thingPersonsStatus] = usePromise<PersonThingRangeResponse[]>(
    async () => {
      if (!thing) return [];
      return restClient.personThing.getCurrentRangesByThing(thing._id);
    },
    [restClient, thing],
  );

  const [thingType, thingTypeError, thingTypeStatus] = usePromise<Undefinable<ThingType>>(
    () => {
      if (!thing) return Promise.resolve(undefined);
      return thingTypeCache.one(thing?.thingTypeId);
    },
    [thing]
  );

  const [lifeCycleState, , lifeCycleStateStatus] = usePromise<ThingLifeCycleStateResponse | undefined>(
    async () => {
      if (!getLifeCycleData || !thing?.sharedThingId) return undefined;
      return restClient.thing.getCurrentLifecycleState(thingId);
    }, [thing, thingId],
  );

  const [lastDataSubscription, lastDataSubscriptionError, lastDataSubscriptionStatus] = usePromise<ThingDataSubscriptionInfoResult | undefined>(
    () => {
      if (!thing) return Promise.resolve(undefined);
      return restClient.thingDataSubscriptionInfo.getThingDataSubscriptionInfo([thing._id]);
    },
    [restClient, thing?._id],
  );

  const statuses = [thingStatus, thingTypeStatus, thingPersonsStatus, lifeCycleStateStatus, lastDataSubscriptionStatus];

  useEffect(() => {
    if (!shouldOpenPopup || isPopupOpen) return;
    if (statuses.includes('pending')) return;
    if (thingId !== entityId || !thing || !thingPersons || !templateLoaded) return;

    const openTimeoutId = window.setTimeout(() => {
      setIsPopupOpen(true);
    }, 0);

    return () => {
      window.clearTimeout(openTimeoutId);
    };
  }, [
    shouldOpenPopup,
    isPopupOpen,
    statuses,
    thingId,
    entityId,
    thing,
    thingPersons,
    templateLoaded,
  ]);

  useEffect(() => {
    const errorCheck = thingError || thingTypeError || thingPersonsError || lastDataSubscriptionError;
    if (!errorCheck) return;
    navigate(`/map/${customRoutesThings}`, {
      state: {
        shouldFly: false,
      },
    });
    enqueueSnackbar(<ErrorMessage error={errorCheck} />, { variant: 'error' });
  }, [thingError, thingTypeError, thingPersonsError, lastDataSubscriptionError]);

  useEffect(() => {
    window.addEventListener('keydown', handleEscapeKeyDown);

    return () => {
      window.removeEventListener('keydown', handleEscapeKeyDown);
    };
  }, [thingId]);

  useEffect(() => {
    if (!isPopupOpen) return;
    map.addEventListener('click', handleMapClick);

    return () => {
      map.removeEventListener('click', handleMapClick);
    };
  }, [isPopupOpen, thingId]);

  useEffect(() => {
    return () => {
      if (navigateAfterCloseTimeoutRef.current !== null) {
        window.clearTimeout(navigateAfterCloseTimeoutRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!thingId) return;
    map.once('markerLoaded', openPopup);
    if (shouldFly) return;
    openPopup();
    return () => {
      map.off('markerLoaded', openPopup);
    };
  }, [thingId, shouldFly]);

  if (statuses.includes('pending')) return <></>;
  if (thingId !== entityId || !thing || !thingPersons || !templateLoaded) return <></>;

  const thingDataSubscription = lastDataSubscription?.[thing._id];
  const faults = thingId ? faultsByThingId[thingId] : undefined;
  const popupFaults = toPopupFaultsContext(faults);

  return (
    <MapPopup
      handleCloseButtonClick={handleCloseButtonClick}
      initialSnapPoint={initialSnapPoint}
      isPopupOpen={isPopupOpen}
      locationData={locationData}
      pane={MapPanes.POPUP_PANE}
      width={theme.mapPopup.width}
    >
      <ThingPopupTemplate context={{ ...thing, lastThingEvent, lastEvents: lastThingEvents, thingType, thingPersons, location: locationData, lifeCycleState, thingDataSubscription, faults: popupFaults, faultStatus: faults }} template={template} />
    </MapPopup>
  );
};
